import React, { useEffect } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import {
  CaseFunctionEnum,
  requestAdmissionInitialData,
  requestAdmissionSchema,
} from 'app/edozvola/cases/request-admission/request-admission.model';
import {
  fetchRequestAdmissionQuery,
  updateRequestAdmissionQuery,
} from 'app/edozvola/cases/request-admission/service/request-admission.query';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Loader } from 'app/shared/loader/loader';
import { Button, FormikDatepicker, Input, Label } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, FormGroup, Row } from 'reactstrap';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { TextArea } from 'edozvola-frontend-lib/dist/ui';
import { fetchCaseFunctionCodebookQuery, fetchEntryLetterDeliveryQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { getCaseContext } from 'app/shared/util/context-util';

const RequestAdmission = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: caseFunctionCodebookOptions, isLoading: caseFunctionCodebookLoading } = fetchCaseFunctionCodebookQuery();

  const { data: deliveryMethodCodebookOptions, isLoading: deliveryMethodCodebookLoading } = fetchEntryLetterDeliveryQuery();
  const { data, isLoading } = fetchRequestAdmissionQuery(uuid);
  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();

  const formik = useFormik({
    initialValues: {
      ...requestAdmissionInitialData,
      ...data,
      caseFunction: data?.caseFunction?.length
        ? data.caseFunction
        : caseFunctionCodebookOptions?.find(option => option.id === CaseFunctionEnum.PODNOSITELJ)
          ? [caseFunctionCodebookOptions.find(option => option.id === CaseFunctionEnum.PODNOSITELJ)]
          : [],
    },
    onSubmit: values => {
      updateRequestAdmission(values);
    },
    validationSchema: requestAdmissionSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const { mutate: updateRequestAdmission, isPending } = updateRequestAdmissionQuery(
    uuid,
    data => {},
    error => {},
  );

  useEffect(() => {
    if (formik.dirty) {
      dispatch(setFormChanged(true));
    } else {
      dispatch(initCaseFormChange());
    }
  }, [formik.dirty]);

  return (
    <div>
      {(isLoading || caseFunctionCodebookLoading || deliveryMethodCodebookLoading) && <Loader />}
      {!(isLoading || caseFunctionCodebookLoading || deliveryMethodCodebookLoading) && (
        <FormikProvider value={formik}>
          <div className="d-flex justify-content-between flex-column gap-3">
            <h6 className="form-header mb-0">Prijemna knjiga</h6>

            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Stvaratelj'} />
                  <PaginatedDropdown
                    placeholder={!sidebarItemPair?.readOnly && 'Odaberi stvaratelja'}
                    onItemSelect={options => formik.setFieldValue('caseFunction', options)}
                    selected={formik.values.caseFunction}
                    labelKey={'value'}
                    options={caseFunctionCodebookOptions}
                    id="caseFunction"
                    onBlur={formik.handleBlur}
                    disabled={true}
                    error={
                      formik.errors && formik.errors.caseFunction && formik.errors.caseFunction[0]
                        ? (formik.errors.caseFunction[0] as string)
                        : ''
                    }
                    showErrorMessage
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Oznaka stvaratelja'} />
                  <Field
                    id="creatorTag"
                    name="creatorTag"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="input"
                        maxLength={100}
                        disabled={formik.values.readonly}
                        value={formik.values.creatorTag ? field.value : ''}
                        error={!formik.values.readonly && formik.errors.creatorTag ? `${formik.errors.creatorTag}` : ''}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Način dostave'} />
                  <PaginatedDropdown
                    placeholder={!formik.values?.readonly && 'Odaberi način dostave'}
                    options={deliveryMethodCodebookOptions}
                    id="entryLetterDelivery"
                    labelKey={'value'}
                    onItemSelect={options => formik.setFieldValue('entryLetterDelivery', options)}
                    onBlur={formik.handleBlur}
                    selected={formik.values.entryLetterDelivery}
                    disabled={formik.values.readonly}
                    error={!formik.values.readonly && (formik.errors?.entryLetterDelivery as string)}
                    showErrorMessage
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Datum primitka'} />
                  <Field
                    id="receiptDate"
                    name="receiptDate"
                    render={({ field }) => (
                      <FormikDatepicker
                        field={field}
                        setFieldValue={formik.setFieldValue}
                        dateFormat={APP_DATE_FORMAT}
                        disabled={formik.values.readonly}
                        error={!formik.values.readonly && formik.errors.receiptDate ? `${formik.errors.receiptDate}` : ''}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Kratki sadržaj'} />
                  <Field
                    id="requestAdmissionShortContent"
                    name="requestAdmissionShortContent"
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        type="textarea"
                        disabled={formik.values.readonly}
                        error={!formik.values.readonly && formik.errors.requestAdmissionShortContent}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <hr />
            <h6 className={'form-header mb-0'}>Podaci o zahtjevu koji je klasificiran</h6>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Klasa akata'} />
                  <Field
                    id="actClassification"
                    name="actClassification"
                    render={({ field }) => (
                      <Input
                        {...field}
                        maxLength={30}
                        value={formik.values.actClassification ? field.value : ''}
                        disabled={formik.values.readonly}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Urudžbeni broj akta'} />
                  <Field
                    id="actOrderNumber"
                    name="actOrderNumber"
                    render={({ field }) => (
                      <Input
                        {...field}
                        maxLength={30}
                        value={formik.values.actOrderNumber ? field.value : ''}
                        disabled={formik.values.readonly}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Datum nastanka akta'} />
                  <Field
                    id="actCreationDate"
                    name="actCreationDate"
                    render={({ field }) => (
                      <FormikDatepicker
                        field={field}
                        setFieldValue={formik.setFieldValue}
                        disabled={formik.values.readonly}
                        dateFormat={APP_DATE_FORMAT}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Ustrojstvena jedinica'} />
                  <Field
                    id="orgUnit"
                    name="orgUnit"
                    render={({ field }) => (
                      <Input
                        {...field}
                        maxLength={500}
                        value={formik.values.orgUnit ? field.value : ''}
                        disabled={formik.values.readonly}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>

          {!sidebarItemPair?.readOnly && (
            <div className={'case-save-button'}>
              <Button
                label={'Spremi'}
                size={'lg'}
                isLoading={isPending}
                disabled={!formik.dirty || !formik.isValid}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </div>
          )}
        </FormikProvider>
      )}
    </div>
  );
};

export default RequestAdmission;
